import React from 'react';
import { Alert, Box, Button, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { DataGate } from '../BotOpsData';
import { getActivity, getPrefs } from '../api/botOpsService';
import { useQuery } from '../hooks/useQuery';
import { ActivityType, Company, Snapshot } from '../lib/types';
import {
  ACTIVITY_TYPES, HeartbeatWindow, SCOPES, Scope, TYPE_LABEL, eventLink, filterTimeline, groupByDay, teamHeartbeat,
} from '../lib/activity';
import { heat } from '../lib/botPage';
import { EXEC_ORDER } from '../lib/types';
import { fmtIct } from '../lib/ict';
import { FONT, WARN_TEXT, HEAT, OVERLINE, TABLE_HEAD_SX } from '../lib/brand';
import { StatusDot } from '../StatusDot';

const SHADES = HEAT;
const SCOPE_LABEL: Record<Scope, string> = { current: 'This company', both: 'Both', lifescribe: 'LifeScribe', quill: 'Quill' };

function Inner({ snapshot }: { snapshot: Snapshot }) {
  const [sp, setSp] = useSearchParams();
  const activity = useQuery(getActivity, []);
  const prefs = useQuery(getPrefs, []);
  const set = (k: string, v: string | null) => setSp((p) => { const n = new URLSearchParams(p); if (v === null || v === '') n.delete(k); else n.set(k, v); return n; }, { replace: true });

  const company: Company = sp.get('company') === 'quill' ? 'quill' : 'lifescribe';
  const scope: Scope = (SCOPES as string[]).includes(sp.get('scope') || '') ? (sp.get('scope') as Scope) : 'both';
  const win: HeartbeatWindow = sp.get('window') === '7d' ? '7d' : '48h';
  const type = (ACTIVITY_TYPES as string[]).includes(sp.get('type') || '') ? (sp.get('type') as ActivityType) : 'all';
  const exec = (EXEC_ORDER as readonly string[]).includes(sp.get('exec') || '') ? sp.get('exec') : null;
  const sinceParam = sp.get('since');
  const since = sinceParam === 'last' ? (prefs.data && prefs.data.last_seen_at) || null : sinceParam && !Number.isNaN(new Date(sinceParam).getTime()) ? sinceParam : null;
  const q = sp.get('q') || '';

  const heartbeat = React.useMemo(() => teamHeartbeat(snapshot, scope, company, win), [snapshot, scope, company, win]);
  const maxCell = Math.max(0, ...heartbeat.flatMap((r) => r.cells.map((c) => c || 0)));
  const feed = activity.data ? activity.data.feed : [];
  const shown = React.useMemo(() => filterTimeline(feed, snapshot, { scope, company, exec, type, since, q }), [feed, snapshot, scope, company, exec, type, since, q]);
  const days = React.useMemo(() => groupByDay(shown.slice(0, 600)), [shown]);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 700, color: 'primary.main', mb: 0.4 }}>Team activity</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>What every bot has been doing. Work events are document and status-file modification times (ICT). Days before the team&apos;s first recorded event show as no data, never as idle.</Typography>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <ToggleButtonGroup exclusive size="small" value={scope} aria-label="Company" onChange={(_e, v: Scope | null) => v && set('scope', v === 'both' ? null : v)}>
          {SCOPES.map((s) => <ToggleButton key={s} value={s} sx={{ textTransform: 'none' }}>{SCOPE_LABEL[s]}</ToggleButton>)}
        </ToggleButtonGroup>
        <ToggleButtonGroup exclusive size="small" value={win} aria-label="Heartbeat window" onChange={(_e, v: HeartbeatWindow | null) => v && set('window', v === '48h' ? null : v)}>
          <ToggleButton value="48h" sx={{ textTransform: 'none' }}>Last 48 h</ToggleButton>
          <ToggleButton value="7d" sx={{ textTransform: 'none' }}>Last 7 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small" aria-label="Team heartbeat" sx={{ minWidth: 900 }}>
          <TableHead sx={TABLE_HEAD_SX}>
            <TableRow><TableCell>Bot</TableCell><TableCell sx={{ width: '38%' }}>Hours (oldest → newest)</TableCell><TableCell>Works every</TableCell><TableCell>Idle for</TableCell><TableCell>JD cadence</TableCell><TableCell>Alert</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {heartbeat.map((r) => (
              <TableRow key={r.bot.slug} hover data-testid="hb-row">
                <TableCell sx={{ whiteSpace: 'nowrap' }}><StatusDot color={r.bot.color} size={9} /> <RouterLink to={`/dashboard/bot-ops/bots/${encodeURIComponent(r.bot.slug)}`}>{r.bot.name}</RouterLink></TableCell>
                <TableCell>
                  <Box role="img" aria-label={`${r.bot.name}: work events by hour`} sx={{ display: 'grid', gridTemplateColumns: `repeat(${r.cells.length}, minmax(0, 1fr))`, gap: '1px' }}>
                    {r.cells.map((c, i) => <Box key={i} sx={{ height: 12, bgcolor: c == null ? 'transparent' : SHADES[heat(c, maxCell)], border: c == null ? '1px dashed' : 'none', borderColor: 'divider' }} />)}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.worksEveryHours != null ? `~${r.worksEveryHours} h` : 'no data'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.idleFor || 'no data'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.jdCadence || 'Not stated in JD'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: WARN_TEXT, fontWeight: 700 }}>{r.alert ? `⚠ ${r.alert}` : '—'}</TableCell>
              </TableRow>
            ))}
            {heartbeat.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No bots with activity data.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>

      <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700, mb: 1 }}>Timeline</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <TextField select size="small" label="Type" value={type} onChange={(e) => set('type', e.target.value === 'all' ? null : e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All types</MenuItem>
          {ACTIVITY_TYPES.map((t) => <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="C-suite" value={exec || 'all'} onChange={(e) => set('exec', e.target.value === 'all' ? null : e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All</MenuItem>
          {EXEC_ORDER.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Search" value={q} onChange={(e) => set('q', e.target.value)} sx={{ minWidth: 200 }} />
        <Tooltip title={prefs.data && prefs.data.last_seen_at ? `Since ${fmtIct(prefs.data.last_seen_at)}` : 'You have not marked anything as seen yet'}>
          <span>
            <Button size="small" variant={sinceParam === 'last' ? 'contained' : 'outlined'} onClick={() => set('since', sinceParam === 'last' ? null : 'last')} disabled={!prefs.data || !prefs.data.last_seen_at}>Since last look</Button>
          </span>
        </Tooltip>
      </Box>

      {activity.error && (
        <Alert severity="error" role="alert" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={activity.reload}>Retry</Button>}>
          Could not load the timeline: {activity.error.message}{feed.length ? ' Showing what was loaded before.' : ''}
        </Alert>
      )}
      {activity.loading && !activity.data && <Typography variant="body2" color="text.secondary" role="status">Loading…</Typography>}
      {activity.data && activity.data.coverage && activity.data.coverage.earliest_event_ict && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Recorded since {fmtIct(activity.data.coverage.earliest_event_ict)}{activity.data.coverage.note ? `. ${activity.data.coverage.note}` : ''}</Typography>
      )}
      {activity.data && shown.length === 0 && <Typography variant="body2" color="text.secondary" data-testid="timeline-empty">Nothing matches these filters.</Typography>}
      {days.map((d) => (
        <Box key={d.day} component="section" sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ ...OVERLINE, display: 'block' }}>{d.day} · {d.events.length}</Typography>
          {d.events.map((e, i) => (
            <Box key={`${e.at}-${i}`} component={RouterLink} to={eventLink(e)} data-testid="timeline-row" sx={{ display: 'flex', gap: 1.2, py: 0.7, borderTop: '1px solid', borderColor: 'divider', textDecoration: 'none', color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}>
              <Box sx={{ width: 64, fontSize: 11.5, color: 'text.secondary', flexShrink: 0 }}>{fmtIct(e.at).split(' · ').slice(-1)[0]}</Box>
              <Box sx={{ width: 110, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{TYPE_LABEL[e.type]}</Box>
              <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, wordBreak: 'break-word' }}>{e.title}</Box>
              <Box sx={{ fontSize: 11.5, color: 'text.secondary', flexShrink: 0 }}>{e.bot_slug}</Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function ActivityPage() {
  return <DataGate>{({ snapshot }) => <Inner snapshot={snapshot} />}</DataGate>;
}
